import React from "react";
import { NavLink, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import InternalTicketFab from "@/components/InternalTicketFab";
import OnboardingTour, { startTour } from "@/components/OnboardingTour";
import {
    LayoutDashboard, Building2, Users2, UsersRound, ShieldCheck, LogOut, UserCircle2, Ticket, Sparkles, Gauge, FileText, Mail, FileQuestion, AlertOctagon, Shield, HelpCircle,
} from "lucide-react";

function initials(name = "") {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((n) => n[0]?.toUpperCase() || "")
        .join("") || "·";
}

const roleLabel = {
    superadmin: "Super Admin",
    admin: "Administrador",
    agent: "Agente",
    customer: "Cliente",
};

export default function AppShell() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const onLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    // Si el usuario tiene must_change_password (post reset por email),
    // se le obliga a definir nueva contraseña antes de usar nada más.
    if (user?.must_change_password && location.pathname !== "/change-password") {
        return <Navigate to="/change-password" replace />;
    }

    const isStaff = user && ["agent", "admin", "superadmin"].includes(user.role);
    const isAdmin = user && ["admin", "superadmin"].includes(user.role);
    const isSuper = user?.role === "superadmin";

    const pageTitles = {
        "/dashboard": "Dashboard",
        "/admin/companies": "Empresas",
        "/admin/agents": "Personas",
        "/admin/groups": "Grupos",
        "/admin/canned-responses": "Plantillas",
        "/admin/ticket-templates": "Tipos de solicitud",
        "/admin/error-logs": "Bitácora de errores",
        "/admin/center": "Centro admin",
        "/admin/email-logs": "Bandeja de envíos",
        "/admin/audit": "Auditoría",
        "/internal/dashboard": "Mundo Interno",
        "/profile": "Mi perfil",
        "/tickets": "Tickets",
        "/reports": "Reportes",
    };
    const currentTitle = Object.entries(pageTitles).find(([p]) => location.pathname.startsWith(p))?.[1] || "itotal4u";

    return (
        <div className="app-shell">
            <aside className="sidebar" data-testid="sidebar">
                <div className="brand">
                    <span className="logo" />
                    <span className="name">itotal<span className="accent">4u</span></span>
                </div>

                <div className="side-section">
                    <div className="side-label">Principal</div>
                    <NavLink to="/dashboard" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-dashboard">
                        <LayoutDashboard size={16} /> Dashboard
                    </NavLink>
                    <NavLink to="/tickets" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-tickets">
                        <Ticket size={16} /> Tickets
                    </NavLink>
                    <NavLink to="/reports" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-reports">
                        <FileText size={16} /> Reportes
                    </NavLink>
                    {isStaff && (
                        <NavLink to="/admin/canned-responses" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-canned">
                            <Sparkles size={16} /> Plantillas
                        </NavLink>
                    )}
                    {user?.is_internal_use && (
                        <NavLink to="/internal/dashboard" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-internal-dashboard">
                            <Shield size={16} /> Mundo Interno
                        </NavLink>
                    )}
                </div>

                {isAdmin && (
                    <div className="side-section">
                        <div className="side-label">Administración</div>
                        <NavLink to="/admin/companies" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-companies">
                            <Building2 size={16} /> Empresas
                        </NavLink>
                        <NavLink to="/admin/agents" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-agents">
                            <Users2 size={16} /> Personas
                        </NavLink>
                        <NavLink to="/admin/groups" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-groups">
                            <UsersRound size={16} /> Grupos
                        </NavLink>
                        <NavLink to="/admin/center" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-center">
                            <Gauge size={16} /> Centro admin
                        </NavLink>
                        <NavLink to="/admin/ticket-templates" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-ticket-templates">
                            <FileQuestion size={16} /> Tipos de solicitud
                        </NavLink>
                        <NavLink to="/admin/error-logs" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-error-logs">
                            <AlertOctagon size={16} /> Bitácora de errores
                        </NavLink>
                        <NavLink to="/admin/email-logs" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-email-logs">
                            <Mail size={16} /> Bandeja de envíos
                        </NavLink>
                        {isSuper && (
                            <NavLink to="/admin/audit" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-audit">
                                <ShieldCheck size={16} /> Auditoría
                            </NavLink>
                        )}
                    </div>
                )}

                <div className="side-section">
                    <div className="side-label">Cuenta</div>
                    <NavLink to="/profile" className={({ isActive }) => "side-link" + (isActive ? " active" : "")} data-testid="nav-profile">
                        <UserCircle2 size={16} /> Mi perfil
                    </NavLink>
                    <button className="side-link" onClick={onLogout} data-testid="nav-logout" style={{ background: "transparent", border: 0, textAlign: "left", font: "inherit", cursor: "pointer" }}>
                        <LogOut size={16} /> Cerrar sesión
                    </button>
                </div>

                <div className="side-footer">
                    <div className="avatar" aria-hidden>{initials(user?.name)}</div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ color: "var(--text)", fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name}</div>
                        <div className="mono" style={{ fontSize: 11 }}>{roleLabel[user?.role] || user?.role}</div>
                    </div>
                </div>
            </aside>

            <div className="main-wrap">
                <header className="topbar">
                    <div className="crumbs" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/settings/branding/logo.bin`}
                            alt="logo"
                            data-testid="topbar-logo"
                            style={{ height: 22, width: "auto", display: "block" }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                        <span style={{ opacity: 0.5 }}>·</span>
                        <b>{currentTitle}</b>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={startTour}
                            title="Iniciar tour guiado de la aplicación"
                            data-testid="topbar-start-tour"
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                background: "transparent", border: "1px solid var(--border)",
                                color: "var(--text-muted)", padding: "6px 10px",
                                borderRadius: 999, fontSize: 12, cursor: "pointer",
                                transition: "background 0.15s ease, color 0.15s ease, border-color 0.15s ease",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text)"; e.currentTarget.style.borderColor = "var(--border-strong)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border)"; }}
                        >
                            <HelpCircle size={13} /> Tour
                        </button>
                        <span className="badge" data-testid="topbar-role">{roleLabel[user?.role] || user?.role}</span>
                    </div>
                </header>
                <main className="main">
                    <Outlet />
                </main>
            </div>
            <InternalTicketFab />
            <OnboardingTour />
        </div>
    );
}
